import { useRef, useState, useEffect } from "react";
import { Music, Play, Pause, X, ChevronLeft, ChevronRight } from "lucide-react";

/* CHERUBIM score player — 4 owner-supplied tracks, visitor opt-in.
   Tiny gold music note in the navbar; expands on tap. */
const TRACKS = [
  { title: "HEAVEN STILL RIDES", src: "/assets/audio/track-1.mp3" },
  { title: "MERCY OVER VENGEANCE", src: "/assets/audio/track-12.mp3" },
  { title: "DON'T SLEEP ON GOD", src: "/assets/audio/track-10.mp3" },
  { title: "THANK YOU FOR MERCY", src: "/assets/audio/track-2.mp3" },
];

const LAST_TRACK_KEY = "cherubim:last-score-track";

const getRememberedTrackIndex = () => {
  try {
    const savedSrc = window.localStorage.getItem(LAST_TRACK_KEY);
    const index = TRACKS.findIndex((track) => track.src === savedSrc);
    return index >= 0 ? index : 0;
  } catch {
    return 0;
  }
};

const rememberTrack = (index) => {
  try {
    window.localStorage.setItem(LAST_TRACK_KEY, TRACKS[index].src);
  } catch {}
};

const MusicPlayer = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const startedRef = useRef(false);
  const playbackTokenRef = useRef(0);
  const autoPausedRef = useRef(false);

  const stopOtherAudio = () => {
    document.querySelectorAll("audio").forEach((el) => {
      if (el !== audioRef.current) {
        el.pause();
        el.removeAttribute("src");
        el.load();
      }
    });
  };

  /* The visitor's last manually chosen selection starts as soon as the page opens.
     Browsers block SOUND before the first user gesture on a fresh domain, so: the
     track starts playing muted at page open (universally allowed) and un-mutes on
     the visitor's first tap / scroll / keypress — already running, zero buffering
     delay. If even muted autoplay is blocked, the same first gesture starts it. */
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    const startIndex = getRememberedTrackIndex();

    const startFirst = () => {
      if (startedRef.current) return;
      const token = ++playbackTokenRef.current;
      stopOtherAudio();
      a.pause();
      a.src = TRACKS[startIndex].src;
      a.load();
      /* Try WITH sound first — browsers allow this on domains the visitor
         already trusts (e.g. the owner's own frequent-preview domain). Only if
         blocked do we start muted and let the first touch bring the sound in. */
      const armed = () => {
        if (startedRef.current || token !== playbackTokenRef.current) return;
        startedRef.current = true;
        setCurrent(startIndex);
        setPlaying(true);
      };
      a.play().then(armed).catch(() => {
        a.muted = true;
        a.play().then(armed).catch(() => {});
      });
    };

    startFirst();
    const onFirstInteraction = () => {
      events.forEach((ev) => window.removeEventListener(ev, onFirstInteraction));
      const el = audioRef.current;
      if (!el) return;
      const wasMuted = el.muted;
      el.muted = false;
      if (startedRef.current) {
        /* Silent head-start must never skip the song's opening —
           the visitor hears it from 0:00 */
        if (wasMuted) el.currentTime = 0;
        if (el.paused) el.play().catch(() => {});
      } else {
        /* Muted start was blocked too — play the already-loaded track now */
        startedRef.current = true;
        stopOtherAudio();
        el.currentTime = 0;
        el.play().then(() => { setCurrent(startIndex); setPlaying(true); }).catch(() => {});
      }
    };
    const events = ["pointerdown", "touchstart", "keydown", "wheel"];
    events.forEach((ev) => window.addEventListener(ev, onFirstInteraction, { once: true }));
    return () => events.forEach((ev) => window.removeEventListener(ev, onFirstInteraction));
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* "ENTER WITH SOUND" on the intro: explicit owner-designed gesture that
     starts the score with sound the instant the site is revealed */
  useEffect(() => {
    const startScore = () => {
      const a = audioRef.current;
      if (!a) return;
      const wasMuted = a.muted;
      const startIndex = getRememberedTrackIndex();
      if (!startedRef.current) {
        startedRef.current = true;
        a.src = TRACKS[startIndex].src;
        a.load();
        setCurrent(startIndex);
      }
      stopOtherAudio();
      a.muted = false;
      if (wasMuted) a.currentTime = 0; /* from the beginning, always */
      a.play().then(() => setPlaying(true)).catch(() => {});
    };
    window.addEventListener("cherubim:start-score", startScore);
    return () => window.removeEventListener("cherubim:start-score", startScore);
  }, []);

  /* Teaser ducking: the score pauses while the teaser plays and resumes after —
     but only if we were the ones who paused it (never overrides a manual pause) */
  useEffect(() => {
    const duck = () => {
      const a = audioRef.current;
      if (!a || a.paused) return;
      autoPausedRef.current = true;
      a.pause();
      setPlaying(false);
    };
    const unduck = () => {
      const a = audioRef.current;
      if (!a || !autoPausedRef.current) return;
      autoPausedRef.current = false;
      a.play().then(() => setPlaying(true)).catch(() => {});
    };
    window.addEventListener("cherubim:pause-score", duck);
    window.addEventListener("cherubim:resume-score", unduck);
    return () => {
      window.removeEventListener("cherubim:pause-score", duck);
      window.removeEventListener("cherubim:resume-score", unduck);
    };
  }, []);

  const playTrack = (i) => {
    const a = audioRef.current;
    if (!a) return;
    const token = ++playbackTokenRef.current;
    startedRef.current = true;
    stopOtherAudio();
    a.pause();
    a.src = TRACKS[i].src;
    a.muted = false;
    a.load();
    setCurrent(i);
    a.play()
      .then(() => { if (token === playbackTokenRef.current) setPlaying(true); })
      .catch(() => { if (token === playbackTokenRef.current) setPlaying(false); });
  };

  const select = (i) => {
    const a = audioRef.current;
    if (!a) return;
    rememberTrack(i);
    if (current === i && playing) {
      a.pause();
      setPlaying(false);
      return;
    }
    playTrack(i);
  };

  const step = (direction) => {
    let next;
    if (current === null) {
      const startIndex = getRememberedTrackIndex();
      next = direction > 0 ? startIndex : (startIndex - 1 + TRACKS.length) % TRACKS.length;
    } else {
      next = (current + direction + TRACKS.length) % TRACKS.length;
    }
    rememberTrack(next);
    playTrack(next);
  };

  const playNextOnEnd = () => {
    playTrack(((current ?? -1) + 1) % TRACKS.length);
  };

  return (
    <>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          aria-label="Previous music selection"
          data-testid="music-prev-button"
          onClick={() => step(-1)}
          className="p-1 text-[var(--cb-gold)] hover:text-[#E2C37A] transition-colors duration-300"
        >
          <ChevronLeft size={16} strokeWidth={1.8} />
        </button>
        <button
          type="button"
          aria-label={open ? "Close music player" : "Open music player"}
          aria-expanded={open}
          data-testid="music-toggle-button"
          onClick={() => setOpen((o) => !o)}
          className={`p-1 transition-colors duration-300 ${playing ? "text-[var(--cb-gold)]" : "text-white/75 hover:text-[var(--cb-gold)]"}`}
        >
          <Music size={19} strokeWidth={1.8} />
        </button>
        <button
          type="button"
          aria-label="Next music selection"
          data-testid="music-next-button"
          onClick={() => step(1)}
          className="p-1 text-[var(--cb-gold)] hover:text-[#E2C37A] transition-colors duration-300"
        >
          <ChevronRight size={16} strokeWidth={1.8} />
        </button>
      </div>

      {open && (
        <div
          data-testid="music-panel"
          className="fixed top-[76px] right-4 z-50 w-[264px] border border-[var(--cb-divider)] shadow-2xl"
          style={{ background: "rgba(12,12,16,0.92)", backdropFilter: "blur(16px)" }}
        >
          <div className="flex items-center justify-between px-4 pt-3.5 pb-2.5 border-b border-[var(--cb-divider)]">
            <span className="font-caps text-[9px] text-[var(--cb-gold)]">THE SCORE</span>
            <button type="button" aria-label="Close music player" data-testid="music-close-button"
                    onClick={() => setOpen(false)}
                    className="text-white/60 hover:text-[var(--cb-gold)] transition-colors duration-300">
              <X size={14} />
            </button>
          </div>
          <div className="p-2">
            {TRACKS.map((t, i) => {
              const isCurrent = current === i;
              return (
                <button
                  key={t.src}
                  type="button"
                  data-testid={`music-track-${i + 1}`}
                  onClick={() => select(i)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors duration-300 hover:bg-white/[0.04]"
                >
                  <span className={isCurrent && playing ? "text-[var(--cb-gold)]" : "text-white/60"}>
                    {isCurrent && playing ? <Pause size={13} /> : <Play size={13} />}
                  </span>
                  <span className={`font-caps text-[10px] tracking-[0.2em] whitespace-nowrap ${isCurrent ? "text-[var(--cb-gold)]" : "text-white/80"}`}>
                    {t.title}
                  </span>
                  {isCurrent && playing && (
                    <span data-testid="music-playing-indicator" className="ml-auto flex items-end gap-[3px] h-3">
                      <span className="w-[2px] bg-[var(--cb-gold)] animate-pulse" style={{ height: "60%" }} />
                      <span className="w-[2px] bg-[var(--cb-gold)] animate-pulse" style={{ height: "100%", animationDelay: "150ms" }} />
                      <span className="w-[2px] bg-[var(--cb-gold)] animate-pulse" style={{ height: "40%", animationDelay: "300ms" }} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div data-testid="music-status" className="px-4 pb-3 pt-1 font-serif italic text-[11px] text-white/45">
            {current === null ? "Choose a selection." : playing ? "Now playing." : "Paused."}
          </div>
        </div>
      )}

      <audio ref={audioRef} src={TRACKS[0].src} preload="auto" playsInline onPlay={stopOtherAudio} onEnded={playNextOnEnd} />
    </>
  );
};

export default MusicPlayer;
